<script setup lang="ts">
import { computed, toRef, watch } from 'vue';
import { api } from '@/api';
import EvaluationsTable from './EvaluationsTable.vue';
import { EvaluationParameterScopeDto, ParameterCombinationTableDto } from '@/api/generator';
import { useField } from 'vee-validate';
import {
  toCreateEvaluationParameterScopeDto,
  toCreateParameterCombinationTableDto,
} from '../mapping';
import { useFormMode } from '@/components/forms/useFormMode';

type EvaluationParameterScopeDtoWithIndex = EvaluationParameterScopeDto & { index: number };

const props = defineProps<{
  name: string; // taskItemAreas[{areaIndex}].evaluationScopes
  targetSystemId?: string;
  combinationTable?: ParameterCombinationTableDto;
}>();

const mode = useFormMode();

const { value: scopes, setValue: replace } = useField<EvaluationParameterScopeDto[]>(
  toRef(() => props.name),
);

// BCC: get the last evaluations from the API, on changed parameters
async function updateEvaluations() {
  if (props.combinationTable && props.targetSystemId) {
    const { result } = await api.taskItemsApi.getTaskItemEvaluationScopesForBcc({
      getTaskItemEvaluationScopeDto: {
        targetSystemId: props.targetSystemId,
        combinationTable: toCreateParameterCombinationTableDto(props.combinationTable),
        previousScopes:
          scopes.value.map((scope) => toCreateEvaluationParameterScopeDto(scope)) ?? null,
      },
    });

    replace(result ?? []);
  } else {
    throw new Error('targetSystemId or combinationTable missing, cant get evaluationScopes');
  }
}

function addIndices(scopes: EvaluationParameterScopeDto[]): EvaluationParameterScopeDtoWithIndex[] {
  return scopes.map((scope, index) => ({ ...scope, index }));
}

const enabledScopes = computed(() => addIndices(scopes.value).filter((scope) => scope.isEnabled));

watch(
  () => [
    props.combinationTable,
    props.combinationTable?.combinations?.map((combination) => combination.isEnabled),
  ],
  ([updatedCombinationTable]) => {
    if (mode.value !== 'details' && updatedCombinationTable) {
      updateEvaluations();
    }
  },
  { immediate: true },
);
</script>

<template>
  <div>
    <v-expansion-panels eager multiple :rounded="true" class="mb-1">
      <EvaluationsTable
        v-for="scope in enabledScopes"
        :key="scope.index"
        :name="`${name}[${scope.index}]`"
      />
    </v-expansion-panels>
  </div>
</template>
